import React from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ClipboardList, Medal, GraduationCap, CalendarDays, Flag, ArrowLeft, ArrowRight,
  CheckCircle2, HelpCircle, ChevronDown, Target, BookOpen, ShieldCheck, Award, Phone,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import FaqSection from '../components/FaqSection';
import { exams, getExam } from '../data/exams';
import { openInquiryPopup } from '../lib/contact';

const iconMap = { ClipboardList, Medal, GraduationCap, CalendarDays, Flag };

function accentClasses(variant) {
  if (variant === 'primary') return { bg: 'bg-primary', text: 'text-primary', bgSoft: 'bg-primary/10' };
  return { bg: 'bg-brand', text: 'text-brand', bgSoft: 'bg-brand/10' };
}

export default function ExamDetail() {
  const { slug } = useParams();
  const exam = getExam(slug);

  if (!exam) {
    return (
      <div className="container mx-auto max-w-3xl px-4 md:px-8 py-24 text-center">
        <h1 className="font-serif text-3xl font-bold text-primary">Exam not found</h1>
        <p className="mt-3 text-muted-foreground">The exam you are looking for does not exist.</p>
        <Link to="/exams" className="mt-6 inline-flex items-center gap-2 text-brand font-bold">
          <ArrowLeft className="w-4 h-4" /> Back to all exams
        </Link>
      </div>
    );
  }

  const Icon = iconMap[exam.icon] || ClipboardList;
  const c = accentClasses(exam.variant);
  const others = exams.filter((e) => e.slug !== exam.slug);

  return (
    <div className="bg-white" data-testid={`exam-detail-${exam.slug}`}>
      <SEO
        title={`${exam.name} — ${exam.subtitle} | Russian Education`}
        description={exam.heroDesc.slice(0, 155)}
        path={`/exams/${exam.slug}`}
        keywords={`${exam.name}, ${exam.subtitle}, MBBS Russia ${exam.short}, ${exam.short} eligibility, ${exam.short} pattern`}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-blue-hero">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cpath fill='%23fff' fill-opacity='0.15' d='M100 20c-8 10-4 22 6 26 8 4 20-2 22-12 2-9-4-18-14-18-4 0-9 1-14 4zm0 40c-15 10-30 30-30 55 0 20 15 40 30 40s30-20 30-40c0-25-15-45-30-55z'/%3E%3C/svg%3E\")", backgroundSize: '160px' }} />
        <div className="container mx-auto max-w-7xl px-4 md:px-8 py-14 md:py-20 relative">
          <div className="text-xs uppercase tracking-widest text-white/70 mb-3">
            <Link to="/" className="hover:text-brand transition-colors">Home</Link>
            <span className="mx-2">›</span>
            <Link to="/exams" className="hover:text-brand transition-colors">Exams</Link>
            <span className="mx-2">›</span>
            <span className="text-white font-semibold">{exam.short}</span>
          </div>
          <div className="text-white max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-2xl bg-brand text-white flex items-center justify-center shadow-xl shadow-brand/40">
                <Icon className="w-8 h-8" />
              </div>
              <div>
                <div className="text-xs md:text-sm uppercase tracking-widest text-white/80 font-semibold">{exam.tagline}</div>
                <div className="text-brand font-serif font-bold text-lg md:text-xl">{exam.subtitle}</div>
              </div>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1] tracking-tight">
              {exam.name}
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/90 max-w-3xl leading-relaxed">
              {exam.heroDesc}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={openInquiryPopup} data-testid="talk-counsellor-btn" className="bg-brand hover:bg-brand-dark text-white font-bold h-12 px-8 rounded-full btn-shift">
                Talk to a Counsellor
              </Button>
              <Link to="/faqs" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white/10 border border-white/30 text-white font-bold hover:bg-white/20 transition-colors">
                See All FAQs <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Facts */}
      <section className="py-12 md:py-14 bg-muted/40">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-brand font-bold">At a Glance</div>
          <h2 className="mt-2 font-serif text-3xl md:text-4xl font-bold text-primary">Quick Facts</h2>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
            {exam.quickFacts.map((f, i) => (
              <div key={i} className="bg-white rounded-xl border border-border p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-[10px] uppercase tracking-widest text-brand font-bold">{f.label}</div>
                <div className="mt-1.5 font-serif text-xl md:text-2xl font-bold text-primary leading-tight">{f.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility + Pattern */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 grid md:grid-cols-2 gap-8">
          <div>
            <div className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] ${c.text} font-bold`}>
              <ShieldCheck className="w-4 h-4" /> Eligibility
            </div>
            <h2 className="mt-2 font-serif text-2xl md:text-3xl font-bold text-primary">Who can appear</h2>
            <ul className="mt-5 space-y-3">
              {exam.eligibility.map((e, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className={`w-5 h-5 shrink-0 mt-0.5 ${c.text}`} />
                  <span className="text-sm md:text-base text-foreground leading-relaxed">{e}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] ${c.text} font-bold`}>
              <Target className="w-4 h-4" /> Exam Pattern
            </div>
            <h2 className="mt-2 font-serif text-2xl md:text-3xl font-bold text-primary">Structure &amp; format</h2>
            <div className="mt-5 rounded-xl border border-border overflow-hidden">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-border">
                  {exam.pattern.map((row, i) => (
                    <tr key={i} className="hover:bg-muted/30">
                      <td className={`px-4 py-3 font-semibold ${c.text} w-1/2`}>{row.label}</td>
                      <td className="px-4 py-3 text-foreground">{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Syllabus */}
      <section className="py-14 md:py-16 bg-muted/40">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] ${c.text} font-bold`}>
            <BookOpen className="w-4 h-4" /> Syllabus
          </div>
          <h2 className="mt-2 font-serif text-3xl md:text-4xl font-bold text-primary">Subjects &amp; topics covered</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {exam.syllabus.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl border border-border p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg ${c.bg} text-white flex items-center justify-center font-bold`}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <h3 className="font-serif text-lg md:text-xl font-bold text-primary">{s.section}</h3>
                </div>
                <ul className="space-y-2">
                  {s.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-foreground">
                      <div className={`w-1.5 h-1.5 rounded-full ${c.bg} mt-2 shrink-0`} />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fees & Validity + Prep tips */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 grid md:grid-cols-[1fr_1.4fr] gap-8">
          <div className="space-y-6">
            <div className={`p-6 rounded-2xl ${c.bgSoft} border border-border`}>
              <div className={`text-[10px] uppercase tracking-widest font-bold ${c.text}`}>Exam Fee</div>
              <div className="mt-1 font-serif text-lg font-bold text-primary leading-snug">{exam.fees}</div>
            </div>
            <div className="p-6 rounded-2xl bg-white border border-border shadow-sm">
              <div className={`text-[10px] uppercase tracking-widest font-bold ${c.text}`}>Certificate Validity</div>
              <div className="mt-1 text-sm text-foreground leading-relaxed">{exam.validity}</div>
            </div>
            <div className="p-6 rounded-2xl bg-white border border-border shadow-sm">
              <div className={`text-[10px] uppercase tracking-widest font-bold ${c.text}`}>After you qualify</div>
              <div className="mt-1 text-sm text-foreground leading-relaxed">{exam.afterQualify}</div>
            </div>
          </div>

          <div>
            <div className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] ${c.text} font-bold`}>
              <Award className="w-4 h-4" /> Preparation Tips
            </div>
            <h2 className="mt-2 font-serif text-2xl md:text-3xl font-bold text-primary">How to prepare effectively</h2>
            <div className="mt-6 space-y-3">
              {exam.prep.map((p, i) => (
                <div key={i} className="flex items-start gap-3 bg-white rounded-xl border border-border p-4 hover:border-brand/40 hover:shadow-sm transition-all">
                  <div className={`shrink-0 w-8 h-8 rounded-lg ${c.bg} text-white text-sm font-bold flex items-center justify-center`}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div className="text-sm md:text-base text-foreground leading-relaxed">{p}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <FaqSection
        eyebrow={`${exam.short} FAQ`}
        title="Frequently Asked Questions"
        subtitle={`Common questions Indian students ask about ${exam.name}.`}
        faqs={exam.faqs}
        variant="blue"
        testid={`exam-faq-${exam.slug}`}
      />

      {/* CTA */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="relative overflow-hidden rounded-2xl bg-primary text-white p-8 md:p-10">
            <div className="absolute inset-0 opacity-10"
              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath fill='%23fff' d='M50 15c-6 6-4 14 3 17 6 3 15-2 15-10 0-6-6-12-13-12-2 0-4 2-5 5z'/%3E%3C/svg%3E\")", backgroundSize: '120px' }} />
            <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <div className="text-xs uppercase tracking-widest text-brand font-bold mb-2">Need Personalised Guidance?</div>
                <h3 className="font-serif text-2xl md:text-3xl font-bold leading-tight">
                  Our senior counsellors can walk you through {exam.name} eligibility, dates and prep plan in a free 30-minute call.
                </h3>
              </div>
              <a href="tel:+919509406802" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-bold h-12 px-7 rounded-full btn-shift shrink-0" data-testid={`exam-call-${exam.slug}`}>
                <Phone className="w-4 h-4" /> Call +91 95094 06802
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Other exams */}
      <section className="py-14 md:py-16 bg-muted/40">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-brand font-bold">Explore More</div>
          <h2 className="mt-2 font-serif text-3xl md:text-4xl font-bold text-primary">Other Exams</h2>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {others.map((o) => {
              const OIcon = iconMap[o.icon] || ClipboardList;
              const oc = accentClasses(o.variant);
              return (
                <Link
                  key={o.slug}
                  to={`/exams/${o.slug}`}
                  data-testid={`related-exam-${o.slug}`}
                  className="group bg-white rounded-xl border border-border p-5 hover:border-brand/40 hover:shadow-lg transition-all"
                >
                  <div className={`w-12 h-12 rounded-lg ${oc.bg} text-white flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <OIcon className="w-5 h-5" />
                  </div>
                  <div className="font-serif font-bold text-primary group-hover:text-brand transition-colors">{o.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{o.subtitle}</div>
                  <div className="mt-3 text-xs text-brand font-semibold inline-flex items-center gap-1">
                    Read details <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
